import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CogIcon, 
  BookOpenIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  EyeIcon,
  DocumentTextIcon,
  FunnelIcon,
  TrashIcon,
  PencilIcon,
  LinkIcon,
  CalendarIcon,
  SparklesIcon,
  EnvelopeIcon,
  ChatBubbleBottomCenterTextIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { isUserAdmin, ADMIN_EMAILS } from '../config/admin';
import ContentManagement from '../components/Admin/PlaybookIndexing';
import { supabase } from '../lib/supabase';

const Admin = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('playbooks');
  const [systemStatus, setSystemStatus] = useState({
    api: { status: 'checking', message: 'Checking...' },
    database: { status: 'checking', message: 'Checking...' },
    search: { status: 'checking', message: 'Checking...' }
  });
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState({
    searchPatterns: [],
    popularQueries: [],
    userBehavior: {},
    sourcePerformance: [],
    queryTypes: {},
    timeRanges: {},
    isLoading: true
  });
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState('7d');
  const [excludeAdmins, setExcludeAdmins] = useState(true);

  // Manage Indexed state
  const [indexedDocuments, setIndexedDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [documentsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [editingType, setEditingType] = useState(null);
  const [editTypeValue, setEditTypeValue] = useState('');
  const [editingSource, setEditingSource] = useState(null);
  const [editSourceValue, setEditSourceValue] = useState('');
  const [savingType, setSavingType] = useState(null);
  const [savingSource, setSavingSource] = useState(null);
  const [savingTitle, setSavingTitle] = useState(null);

  const isAdmin = isUserAdmin(user);

  // Analytics data effect
  useEffect(() => {
    if (activeSection === 'analytics') {
      fetchAnalyticsData();
    }
  }, [activeSection, analyticsTimeRange, excludeAdmins]);

  // Manage Indexed effect
  useEffect(() => {
    if (activeSection === 'manage') {
      fetchIndexedDocuments();
    }
  }, [activeSection]);

  // Filter and sort documents
  useEffect(() => {
    let filtered = [...indexedDocuments];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.url?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.content_source?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(doc => doc.type === selectedType);
    }

    // Source filter
    if (selectedSource !== 'all') {
      filtered = filtered.filter(doc => doc.content_source === selectedSource);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.timestamp || b.created_at) - new Date(a.timestamp || a.created_at);
        case 'oldest':
          return new Date(a.timestamp || a.created_at) - new Date(b.timestamp || b.created_at);
        case 'title':
          return a.title.localeCompare(b.title);
        case 'type':
          return a.type.localeCompare(b.type);
        default:
          return 0;
      }
    });

    setFilteredDocuments(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [indexedDocuments, searchQuery, selectedType, selectedSource, sortBy]);

  const fetchAnalyticsData = async () => {
    try {
      setAnalyticsData(prev => ({ ...prev, isLoading: true }));
      
      console.log('📊 Fetching search analytics directly from database...');
      
      // Get admin user IDs once if admin filtering is enabled
      let adminUserIds = [];
      if (excludeAdmins) {
        const { data: adminUsers, error: adminError } = await supabase
          .from('profiles')
          .select('id')
          .in('email', ADMIN_EMAILS);
        
        if (adminError) {
          console.warn('Could not fetch admin user IDs, proceeding without admin filter:', adminError);
        } else if (adminUsers && adminUsers.length > 0) {
          adminUserIds = adminUsers.map(user => user.id);
          console.log(`🚫 Will exclude ${adminUserIds.length} admin users from analytics`);
        }
      }
      
      // Calculate date range
      const now = new Date();
      const startDate = new Date();
      switch (analyticsTimeRange) {
        case '1d':
          startDate.setDate(now.getDate() - 1);
          break;
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(now.getDate() - 90);
          break;
        default:
          startDate.setDate(now.getDate() - 7);
      }
      
      // Get popular queries with optional admin exclusion
      let popularQueriesQuery = supabase
        .from('chat_interactions')
        .select('question, query_type, timestamp, user_id')
        .gte('timestamp', startDate.toISOString())
        .not('question', 'is', null);
      
      // Exclude admin users if filter is enabled
      if (excludeAdmins && adminUserIds.length > 0) {
        popularQueriesQuery = popularQueriesQuery.not('user_id', 'in', `(${adminUserIds.join(',')})`);
      }
      
      const { data: popularQueriesData, error: queriesError } = await popularQueriesQuery
        .order('timestamp', { ascending: false });
      
      if (queriesError) {
        console.error('Error fetching queries:', queriesError);
        throw queriesError;
      }

      // Get chat mode usage from chat_interactions (direct and accurate) with optional admin exclusion
      let modeUsageQuery = supabase
        .from('chat_interactions')
        .select('chat_mode, timestamp, user_id')
        .gte('timestamp', startDate.toISOString())
        .not('chat_mode', 'is', null);
      
      // Exclude admin users if filter is enabled
      if (excludeAdmins && adminUserIds.length > 0) {
        modeUsageQuery = modeUsageQuery.not('user_id', 'in', `(${adminUserIds.join(',')})`);
      }
      
      const { data: modeUsageData, error: modeError } = await modeUsageQuery;
      
      if (modeError) {
        console.error('Error fetching mode usage:', modeError);
        throw modeError;
      }

      // Process chat mode usage
      const modeUsage = {
        search: 0,
        coach: 0,
        total: 0
      };

      // Count mode usage directly from chat_interactions
      modeUsageData?.forEach(interaction => {
        if (interaction.chat_mode === 'search') {
          modeUsage.search++;
        } else if (interaction.chat_mode === 'coach') {
          modeUsage.coach++;
        }
        modeUsage.total++;
      });
      
      // Process popular queries
      const queryFrequency = {};
      const queryTypes = {};
      popularQueriesData?.forEach(interaction => {
        const query = interaction.question.toLowerCase().trim();
        if (query.length > 0) {
          queryFrequency[query] = (queryFrequency[query] || 0) + 1;
          if (interaction.query_type) {
            queryTypes[interaction.query_type] = (queryTypes[interaction.query_type] || 0) + 1;
          }
        }
      });
      
      const popularQueries = Object.entries(queryFrequency)
        .map(([query, count]) => {
          const latestInteraction = popularQueriesData?.find(i => 
            i.question.toLowerCase().trim() === query
          );
          return {
            query: query.length > 100 ? query.substring(0, 100) + '...' : query,
            search_count: count,
            query_type: latestInteraction?.query_type || null,
            latest_search: latestInteraction?.timestamp
          };
        })
        .sort((a, b) => b.search_count - a.search_count);
      
      // Get user behavior metrics with optional admin exclusion
      let behaviorQuery = supabase
        .from('chat_interactions')
        .select('user_id, response_time, source_count, timestamp')
        .gte('timestamp', startDate.toISOString());
      
      // Exclude admin users if filter is enabled
      if (excludeAdmins && adminUserIds.length > 0) {
        behaviorQuery = behaviorQuery.not('user_id', 'in', `(${adminUserIds.join(',')})`);
      }
      
      const { data: behaviorData, error: behaviorError } = await behaviorQuery;
      
      if (behaviorError) {
        console.error('Error fetching behavior data:', behaviorError);
        throw behaviorError;
      }
      
      const uniqueUsers = new Set(behaviorData?.filter(b => b.user_id).map(b => b.user_id)).size;
      const totalQueries = behaviorData?.length || 0;
      const avgResponseTime = behaviorData?.filter(b => b.response_time).length > 0 
        ? Math.round(behaviorData.filter(b => b.response_time).reduce((sum, b) => sum + b.response_time, 0) / behaviorData.filter(b => b.response_time).length)
        : null;
      const avgSourcesPerQuery = behaviorData?.filter(b => b.source_count && b.source_count > 0).length > 0
        ? (behaviorData.filter(b => b.source_count && b.source_count > 0).reduce((sum, b) => sum + b.source_count, 0) / behaviorData.filter(b => b.source_count && b.source_count > 0).length).toFixed(1)
        : 0;
      
      // Get source performance from metadata with optional admin exclusion
      let sourceQuery = supabase
        .from('chat_interactions')
        .select('metadata, user_id')
        .gte('timestamp', startDate.toISOString())
        .not('metadata', 'is', null);
      
      // Exclude admin users if filter is enabled
      if (excludeAdmins && adminUserIds.length > 0) {
        sourceQuery = sourceQuery.not('user_id', 'in', `(${adminUserIds.join(',')})`);
      }
      
      const { data: sourceData, error: sourceError } = await sourceQuery;
      
      if (sourceError) {
        console.error('Error fetching source data:', sourceError);
        throw sourceError;
      }
      
      const sourceUsage = {};
      sourceData?.forEach(interaction => {
        if (interaction.metadata?.sources_info) {
          interaction.metadata.sources_info.forEach(source => {
            const key = `${source.title}|${source.type || 'unknown'}`;
            if (!sourceUsage[key]) {
              sourceUsage[key] = {
                title: source.title,
                type: source.type || 'unknown',
                usage_count: 0,
                total_confidence: 0
              };
            }
            sourceUsage[key].usage_count++;
            sourceUsage[key].total_confidence += source.confidence || 0;
          });
        }
      });
      
      const sourcePerformance = Object.values(sourceUsage)
        .map(source => ({
          ...source,
          avg_confidence: source.usage_count > 0 ? (source.total_confidence / source.usage_count).toFixed(3) : 0
        }))
        .sort((a, b) => b.usage_count - a.usage_count);
      
      // Identify search patterns with mode breakdown
      const searchPatterns = [];
      const patterns = [
        { pattern: 'how to', description: 'How-to questions' },
        { pattern: 'what is', description: 'Definitional questions' },
        { pattern: 'best practices', description: 'Best practice inquiries' },
        { pattern: 'examples', description: 'Example requests' },
        { pattern: 'template', description: 'Template requests' },
        { pattern: 'process', description: 'Process inquiries' },
        { pattern: 'framework', description: 'Framework questions' },
        { pattern: 'checklist', description: 'Checklist requests' }
      ];

      // Get chat interactions with mode for pattern analysis with optional admin exclusion
      let patternQuery = supabase
        .from('chat_interactions')
        .select('question, chat_mode, timestamp, user_id')
        .gte('timestamp', startDate.toISOString())
        .not('question', 'is', null);
      
      // Exclude admin users if filter is enabled
      if (excludeAdmins && adminUserIds.length > 0) {
        patternQuery = patternQuery.not('user_id', 'in', `(${adminUserIds.join(',')})`);
      }
      
      const { data: patternAnalysisData, error: patternError } = await patternQuery;
      
      if (patternError) {
        console.error('Error fetching pattern analysis data:', patternError);
        throw patternError;
      }

      console.log(`📊 Pattern analysis: ${patternAnalysisData?.length || 0} interactions with questions`);
      
      patterns.forEach(patternDef => {
        // Use chat_interactions directly for both volume and mode data
        const matches = patternAnalysisData?.filter(interaction => 
          interaction.question.toLowerCase().includes(patternDef.pattern)
        ) || [];
        
        if (matches.length > 0) {
          // Count by mode directly from chat_interactions
          const modeBreakdown = {
            search: 0,
            coach: 0,
            unknown: 0,
            total: matches.length
          };

          matches.forEach(interaction => {
            if (interaction.chat_mode === 'search') {
              modeBreakdown.search++;
            } else if (interaction.chat_mode === 'coach') {
              modeBreakdown.coach++;
            } else {
              modeBreakdown.unknown++;
            }
          });

          searchPatterns.push({
            pattern: patternDef.description,
            frequency: matches.length,
            modeBreakdown: modeBreakdown,
            example: matches[0]?.question.substring(0, 80) + (matches[0]?.question.length > 80 ? '...' : '')
          });
        }
      });
      
      searchPatterns.sort((a, b) => b.frequency - a.frequency);
      
      const analyticsResult = {
        popularQueries,
        userBehavior: {
          totalQueries,
          uniqueUsers,
          avgResponseTime,
          avgSourcesPerQuery: parseFloat(avgSourcesPerQuery)
        },
        queryTypes,
        modeUsage, // Add mode usage data
        sourcePerformance,
        searchPatterns,
        timeRange: {
          start: startDate.toISOString(),
          end: now.toISOString(),
          range: analyticsTimeRange
        }
      };
      
      console.log(`📊 Analytics processed: ${totalQueries} queries, ${uniqueUsers} users, ${sourcePerformance.length} sources, ${modeUsage.total} mode interactions${excludeAdmins ? ` (admin users excluded: ${adminUserIds.length})` : ' (includes admin users)'}`);
      
      setAnalyticsData({
        ...analyticsResult,
        isLoading: false
      });
      
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setAnalyticsData(prev => ({ 
        ...prev, 
        isLoading: false,
        error: 'Failed to load analytics data'
      }));
    }
  };

  const fetchIndexedDocuments = async () => {
    try {
      setDocumentsLoading(true);
      console.log('📋 Fetching indexed documents...');
      
      const { data: documents, error } = await supabase
        .from('documents')
        .select(`
          id,
          title,
          content_source,
          type,
          url,
          metadata,
          created_at
        `)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('❌ Error fetching documents:', error);
        throw error;
      }
      
      console.log(`📋 Found ${documents?.length || 0} documents`);
      
      // Get chunk counts separately
      const documentsWithChunks = await Promise.all(
        documents.map(async (doc) => {
          const { count } = await supabase
            .from('document_chunks')
            .select('*', { count: 'exact', head: true })
            .eq('document_id', doc.id);
          
          return {
            ...doc,
            chunk_count: count || 0
          };
        })
      );
      
      setIndexedDocuments(documentsWithChunks);
      
    } catch (error) {
      console.error('Error fetching indexed documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId);

      if (error) {
        console.error('Error deleting document:', error);
        return;
      }

      // Remove from local state
      setIndexedDocuments(prev => prev.filter(doc => doc.id !== documentId));
      console.log('✅ Document deleted successfully');
      
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const startEditingTitle = (doc) => {
    setEditingTitle(doc.id);
    setEditTitleValue(doc.title);
  };

  const cancelEditingTitle = () => {
    setEditingTitle(null);
    setEditTitleValue('');
  };

  // Note: saveTitle function removed - using autoSaveTitle for direct input changes

  const autoSaveTitle = async (documentId, newTitle) => {
    try {
      setSavingTitle(documentId);
      console.log('🔄 Auto-saving title:', newTitle);
      
      // Direct Supabase call - simple and efficient
      const { error } = await supabase
        .from('documents')
        .update({ title: newTitle.trim() })
        .eq('id', documentId);

      if (error) {
        console.error('Error auto-saving title:', error);
        return;
      }

      // Update local state
      setIndexedDocuments(prev => 
        prev.map(doc => 
          doc.id === documentId 
            ? { ...doc, title: newTitle.trim() }
            : doc
        )
      );

      setEditingTitle(null);
      setEditTitleValue('');
      console.log('✅ Title auto-saved successfully');
      
    } catch (error) {
      console.error('Error auto-saving title:', error);
    } finally {
      setSavingTitle(null);
    }
  };

  const startEditingType = (doc) => {
    setEditingType(doc.id);
    setEditTypeValue(doc.type);
  };

  const cancelEditingType = () => {
    setEditingType(null);
    setEditTypeValue('');
  };

  // Note: saveType function removed - using autoSaveType for direct dropdown changes

  const autoSaveType = async (documentId, newType) => {
    try {
      setSavingType(documentId);
      console.log('🔄 Auto-saving type:', { documentId, newType, supabaseClient: !!supabase });
      
      // Debug: Check current user authentication and admin status
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      console.log('👤 Current authenticated user:', {
        userId: user?.id,
        email: user?.email,
        userError: userError?.message,
        hasUser: !!user
      });
      
      if (user?.email) {
        // Test admin function
        try {
          const { data: isAdminResult, error: adminError } = await supabase.rpc('is_admin', { 
            user_email: user.email 
          });
          console.log('🔑 Admin check result:', {
            userEmail: user.email,
            isAdmin: isAdminResult,
            adminError: adminError?.message
          });
        } catch (adminFuncError) {
          console.log('⚠️ Admin function not available:', adminFuncError.message);
        }
      }
      
      // First, check if the document exists
      const { data: existingDoc, error: checkError } = await supabase
        .from('documents')
        .select('id, title, type')
        .eq('id', documentId)
        .single();
        
      console.log('🔍 Document existence check:', { 
        existingDoc, 
        checkError,
        queriedDocumentId: documentId,
        foundDocumentId: existingDoc?.id,
        foundTitle: existingDoc?.title,
        currentType: existingDoc?.type,
        targetType: newType
      });
      
      if (checkError || !existingDoc) {
        console.error('❌ Document not found in database:', { documentId, checkError });
        alert(`Document not found in database: ${documentId}`);
        return;
      }
      
      console.log('✅ Document exists, proceeding with update...', {
        updateDocumentId: documentId,
        existingDocumentId: existingDoc.id,
        idsMatch: documentId === existingDoc.id
      });
      
      // Direct Supabase call - simple and efficient
      const { data, error } = await supabase
        .from('documents')
        .update({ type: newType })
        .eq('id', documentId)
        .select(); // Add select to get updated data back

      console.log('📊 Supabase update result:', { 
        data, 
        error,
        updateAttemptId: documentId,
        updateData: { type: newType },
        rowsAffected: data?.length || 0
      });

      if (error) {
        console.error('❌ Error auto-saving type:', error);
        alert(`Failed to save type: ${error.message}`);
        return;
      }

      if (!data || data.length === 0) {
        console.warn('⚠️ No rows updated - document may not exist');
        alert('No document was updated - please check if the document exists');
        return;
      }

      console.log('✅ Database updated successfully:', data[0]);

      // Update local state
      setIndexedDocuments(prev => 
        prev.map(doc => 
          doc.id === documentId 
            ? { ...doc, type: newType }
            : doc
        )
      );

      setEditingType(null);
      setEditTypeValue('');
      console.log('✅ Type auto-saved successfully and UI updated');
      
    } catch (error) {
      console.error('💥 Exception during auto-save:', error);
      alert(`Exception during save: ${error.message}`);
    } finally {
      setSavingType(null);
    }
  };

  const startEditingSource = (doc) => {
    setEditingSource(doc.id);
    setEditSourceValue(doc.content_source);
  };

  const cancelEditingSource = () => {
    setEditingSource(null);
    setEditSourceValue('');
  };

  // Note: saveSource function removed - using autoSaveSource for direct dropdown changes

  const autoSaveSource = async (documentId, newSource) => {
    try {
      setSavingSource(documentId);
      console.log('🔄 Auto-saving source:', newSource);
      
      // Direct Supabase call - simple and efficient
      const { error } = await supabase
        .from('documents')
        .update({ content_source: newSource })
        .eq('id', documentId);

      if (error) {
        console.error('Error auto-saving source:', error);
        return;
      }

      // Update local state
      setIndexedDocuments(prev => 
        prev.map(doc => 
          doc.id === documentId 
            ? { ...doc, content_source: newSource }
            : doc
        )
      );

      setEditingSource(null);
      setEditSourceValue('');
      console.log('✅ Source auto-saved successfully');
      
    } catch (error) {
      console.error('Error auto-saving source:', error);
    } finally {
      setSavingSource(null);
    }
  };

  // Check system status
  useEffect(() => {
    const checkSystemStatus = async () => {
      const newStatus = { ...systemStatus };

      // Check API
      try {
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: 'test' })
        });
        
        if (response.ok) {
          newStatus.api = { 
            status: 'success', 
            message: 'Connected' 
          };
        } else {
          newStatus.api = { 
            status: 'error', 
            message: `HTTP ${response.status}` 
          };
        }
      } catch (error) {
        newStatus.api = { 
          status: 'error', 
          message: 'Connection failed' 
        };
      }

      // Check database (via content stats)
      try {
        const response = await fetch('/api/index-playbooks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'get_content_stats' })
        });
        if (response.ok) {
          newStatus.database = { 
            status: 'success', 
            message: 'Active' 
          };
        } else {
          newStatus.database = { 
            status: 'error', 
            message: 'Connection failed' 
          };
        }
      } catch (error) {
        newStatus.database = { 
          status: 'error', 
          message: 'Connection failed' 
        };
      }

      // Check search (vector service)
      try {
        const response = await fetch('/api/index-playbooks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'get_content_stats' })
        });
        if (response.ok) {
          newStatus.search = { 
            status: 'success', 
            message: 'Ready' 
          };
        } else {
          newStatus.search = { 
            status: 'error', 
            message: 'Not available' 
          };
        }
      } catch (error) {
        newStatus.search = { 
          status: 'error', 
          message: 'Not available' 
        };
      }

      setSystemStatus(newStatus);
    };

    if (isAdmin) {
      checkSystemStatus();
    }
  }, [isAdmin]);

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-gray-400 mb-6">
            You don't have permission to access the admin dashboard.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const sections = [
    {
      id: 'playbooks',
      name: 'Content Management',
      icon: <BookOpenIcon className="w-5 h-5" />,
      description: 'Index playbooks, external URLs, and public Notion pages'
    },
    {
      id: 'analytics',
      name: 'Analytics',
      icon: <ChartBarIcon className="w-5 h-5" />,
      description: 'Search patterns and usage insights'
    },
    {
      id: 'manage',
      name: 'Manage Indexed',
      icon: <DocumentTextIcon className="w-5 h-5" />,
      description: 'Browse, search, and manage all indexed documents'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <CogIcon className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          </div>
          <p className="text-gray-400">Manage content that powers the AI chat for all users</p>
          <div className="mt-2 text-sm text-gray-500">
            Logged in as: {user?.email}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => !section.disabled && setActiveSection(section.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                    activeSection === section.id
                      ? 'bg-gradient-to-r from-primary to-purple-500 text-white shadow-lg'
                      : section.disabled
                      ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                  disabled={section.disabled}
                >
                  <div className="flex items-center space-x-3">
                    {section.icon}
                    <div className="flex-1">
                      <div className="font-medium">{section.name}</div>
                      <div className="text-xs opacity-75 mt-1">{section.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </nav>

            {/* Quick Stats */}
            <div className="mt-8 bg-gray-800 rounded-xl p-4">
              <h3 className="font-medium text-white mb-3">System Status</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">API</span>
                  <div className="flex items-center space-x-2">
                    {systemStatus.api.status === 'success' && <CheckCircleIcon className="w-4 h-4 text-green-400" />}
                    {systemStatus.api.status === 'error' && <XCircleIcon className="w-4 h-4 text-red-400" />}
                    {systemStatus.api.status === 'checking' && <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />}
                    <span className={systemStatus.api.status === 'success' ? 'text-green-400' : systemStatus.api.status === 'error' ? 'text-red-400' : 'text-blue-400'}>
                      {systemStatus.api.message}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Database</span>
                  <div className="flex items-center space-x-2">
                    {systemStatus.database.status === 'success' && <CheckCircleIcon className="w-4 h-4 text-green-400" />}
                    {systemStatus.database.status === 'error' && <XCircleIcon className="w-4 h-4 text-red-400" />}
                    {systemStatus.database.status === 'checking' && <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />}
                    <span className={systemStatus.database.status === 'success' ? 'text-green-400' : systemStatus.database.status === 'error' ? 'text-red-400' : 'text-blue-400'}>
                      {systemStatus.database.message}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Search</span>
                  <div className="flex items-center space-x-2">
                    {systemStatus.search.status === 'success' && <CheckCircleIcon className="w-4 h-4 text-green-400" />}
                    {systemStatus.search.status === 'error' && <XCircleIcon className="w-4 h-4 text-red-400" />}
                    {systemStatus.search.status === 'checking' && <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />}
                    <span className={systemStatus.search.status === 'success' ? 'text-green-400' : systemStatus.search.status === 'error' ? 'text-red-400' : 'text-blue-400'}>
                      {systemStatus.search.message}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">User</span>
                  <span className="text-blue-400">{user?.email}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {activeSection === 'playbooks' && (
                <div>
                  <ContentManagement />
                </div>
              )}

              {/* Analytics Section */}
              {activeSection === 'analytics' && (
                <div>
                  {/* Time Range Selector */}
                  <div className="mb-6 p-4 bg-gray-700/30 rounded-xl">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-white">Search Analytics</h3>
                                        <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-300">Time Range:</label>
                      <select
                        value={analyticsTimeRange}
                        onChange={(e) => setAnalyticsTimeRange(e.target.value)}
                        className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-primary"
                      >
                        <option value="1d">Last 24 hours</option>
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={excludeAdmins}
                          onChange={(e) => setExcludeAdmins(e.target.checked)}
                          className="w-4 h-4 text-primary bg-gray-700 border-gray-600 rounded focus:ring-primary focus:ring-2"
                        />
                        <span className="text-sm text-gray-300">Exclude admin users</span>
                      </label>
                    </div>
                    
                    <button
                      onClick={fetchAnalyticsData}
                      disabled={analyticsData.isLoading}
                      className="flex items-center space-x-1 px-2 py-1 bg-primary hover:bg-primary-dark text-white rounded text-sm transition-colors"
                    >
                      <ArrowPathIcon className={`w-4 h-4 ${analyticsData.isLoading ? 'animate-spin' : ''}`} />
                      <span>Refresh</span>
                    </button>
                  </div>
                    </div>
                  </div>

                  {analyticsData.isLoading ? (
                    <div className="text-center py-12">
                      <ArrowPathIcon className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-400">Loading analytics data...</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Overview Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div className="bg-gradient-to-br from-blue-900/30 to-gray-800/50 rounded-lg p-4 border border-blue-700/30">
                          <div className="flex items-center space-x-3">
                            <MagnifyingGlassIcon className="w-8 h-8 text-blue-400" />
                            <div>
                              <p className="text-2xl font-bold text-white">{analyticsData.userBehavior?.totalQueries || 0}</p>
                              <p className="text-sm text-blue-300">Total Searches</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-green-900/30 to-gray-800/50 rounded-lg p-4 border border-green-700/30">
                          <div className="flex items-center space-x-3">
                            <UserGroupIcon className="w-8 h-8 text-green-400" />
                            <div>
                              <p className="text-2xl font-bold text-white">{analyticsData.userBehavior?.uniqueUsers || 0}</p>
                              <p className="text-sm text-green-300">Unique Users</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-purple-900/30 to-gray-800/50 rounded-lg p-4 border border-purple-700/30">
                          <div className="flex items-center space-x-3">
                            <ClockIcon className="w-8 h-8 text-purple-400" />
                            <div>
                              <p className="text-2xl font-bold text-white">{analyticsData.userBehavior?.avgResponseTime ? `${analyticsData.userBehavior.avgResponseTime}ms` : 'N/A'}</p>
                              <p className="text-sm text-purple-300">Avg Response Time</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-orange-900/30 to-gray-800/50 rounded-lg p-4 border border-orange-700/30">
                          <div className="flex items-center space-x-3">
                            <ArrowTrendingUpIcon className="w-8 h-8 text-orange-400" />
                            <div>
                              <p className="text-2xl font-bold text-white">{analyticsData.userBehavior?.avgSourcesPerQuery || 0}</p>
                              <p className="text-sm text-orange-300">Avg Sources Used</p>
                            </div>
                          </div>
                        </div>

                        {/* New Mode Usage Card */}
                        <div className="bg-gradient-to-br from-indigo-900/30 to-gray-800/50 rounded-lg p-4 border border-indigo-700/30">
                          <div className="flex items-center space-x-3">
                            <ChatBubbleBottomCenterTextIcon className="w-8 h-8 text-indigo-400" />
                            <div>
                              <p className="text-2xl font-bold text-white">{analyticsData.modeUsage?.total || 0}</p>
                              <p className="text-sm text-indigo-300">Mode Interactions</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Chat Mode Usage Breakdown */}
                      <div className="bg-gray-700/20 rounded-xl p-6">
                        <h4 className="font-medium text-white mb-4 flex items-center space-x-2">
                          <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-indigo-400" />
                          <span>Chat Mode Usage</span>
                        </h4>
                        {analyticsData.modeUsage && analyticsData.modeUsage.total > 0 ? (
                          <div className="space-y-4">
                            {/* Mode Usage Stats */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                      </svg>
                                    </div>
                                    <div>
                                      <p className="text-white font-medium">Search Mode</p>
                                      <p className="text-sm text-gray-400">Find resources instantly</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-2xl font-bold text-primary">{analyticsData.modeUsage.search || 0}</p>
                                    <p className="text-xs text-gray-400">
                                      {analyticsData.modeUsage.total > 0 ? Math.round((analyticsData.modeUsage.search / analyticsData.modeUsage.total) * 100) : 0}% of total
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/20">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                                      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                      </svg>
                                    </div>
                                    <div>
                                      <p className="text-white font-medium">Coach Mode</p>
                                      <p className="text-sm text-gray-400">Guided problem-solving</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-2xl font-bold text-green-400">{analyticsData.modeUsage.coach || 0}</p>
                                    <p className="text-xs text-gray-400">
                                      {analyticsData.modeUsage.total > 0 ? Math.round((analyticsData.modeUsage.coach / analyticsData.modeUsage.total) * 100) : 0}% of total
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Visual Bar Chart */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-400">Mode Distribution</span>
                                <span className="text-gray-400">Total: {analyticsData.modeUsage.total} interactions</span>
                              </div>
                              <div className="w-full bg-gray-800 rounded-full h-4 overflow-hidden">
                                <div className="h-full flex">
                                  {analyticsData.modeUsage.total > 0 && (
                                    <>
                                      <div 
                                        className="bg-primary h-full transition-all duration-300"
                                        style={{ width: `${(analyticsData.modeUsage.search / analyticsData.modeUsage.total) * 100}%` }}
                                      />
                                      <div 
                                        className="bg-green-500 h-full transition-all duration-300"
                                        style={{ width: `${(analyticsData.modeUsage.coach / analyticsData.modeUsage.total) * 100}%` }}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                <span>Search Mode</span>
                                <span>Coach Mode</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-gray-400 text-center py-4">No mode usage data available for this time period</p>
                        )}
                      </div>

                      {/* Popular Search Queries */}
                      <div className="bg-gray-700/20 rounded-xl p-6">
                        <h4 className="font-medium text-white mb-4 flex items-center space-x-2">
                          <ArrowTrendingUpIcon className="w-5 h-5 text-green-400" />
                          <span>Most Popular Queries</span>
                        </h4>
                        {analyticsData.popularQueries?.length > 0 ? (
                          <div className="space-y-3">
                            {analyticsData.popularQueries.slice(0, 10).map((query, index) => (
                              <div key={index} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                                <div className="flex-1">
                                  <p className="text-white font-medium">{query.query}</p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {query.query_type && (
                                      <span className="px-2 py-1 bg-gray-700 rounded text-xs mr-2">
                                        {query.query_type}
                                      </span>
                                    )}
                                    Last searched: {new Date(query.latest_search).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-primary">{query.search_count}</p>
                                  <p className="text-xs text-gray-400">searches</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-400 text-center py-4">No search data available for this time period</p>
                        )}
                      </div>

                      {/* Search Patterns by Type */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-gray-700/20 rounded-xl p-6">
                          <h4 className="font-medium text-white mb-4 flex items-center space-x-2">
                            <ChartBarIcon className="w-5 h-5 text-blue-400" />
                            <span>Query Types</span>
                          </h4>
                          {analyticsData.queryTypes && Object.keys(analyticsData.queryTypes).length > 0 ? (
                            <div className="space-y-3">
                              {Object.entries(analyticsData.queryTypes)
                                .sort(([,a], [,b]) => b - a)
                                .map(([type, count]) => (
                                <div key={type} className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                                    <span className="text-white capitalize">{type.replace('_', ' ')}</span>
                                  </div>
                                  <span className="text-gray-300 font-medium">{count}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-400 text-center py-4">No query type data available</p>
                          )}
                        </div>

                        <div className="bg-gray-700/20 rounded-xl p-6">
                          <h4 className="font-medium text-white mb-4 flex items-center space-x-2">
                            <EyeIcon className="w-5 h-5 text-purple-400" />
                            <span>Source Performance</span>
                          </h4>
                          {analyticsData.sourcePerformance?.length > 0 ? (
                            <div className="space-y-3">
                              {analyticsData.sourcePerformance.slice(0, 8).map((source, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-gray-800/30 rounded">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-white text-sm font-medium truncate">{source.title}</p>
                                    <p className="text-xs text-gray-400">
                                      <span className={`px-1 py-0.5 rounded text-xs ${
                                        source.type === 'playbook' ? 'bg-green-900/30 text-green-300' :
                                        source.type === 'openverse_resource' ? 'bg-purple-900/30 text-purple-300' :
                                        source.type === 'newsletter' ? 'bg-yellow-900/30 text-yellow-300' :
                                        'bg-gray-700 text-gray-300'
                                      }`}>
                                        {source.type}
                                      </span>
                                    </p>
                                  </div>
                                  <div className="text-right ml-2">
                                    <p className="text-sm font-bold text-primary">{source.usage_count}</p>
                                    <p className="text-xs text-gray-400">uses</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-400 text-center py-4">No source performance data available</p>
                          )}
                        </div>
                      </div>

                      {/* Search Patterns */}
                      <div className="bg-gray-700/20 rounded-xl p-6">
                        <h4 className="font-medium text-white mb-4 flex items-center space-x-2">
                          <MagnifyingGlassIcon className="w-5 h-5 text-green-400" />
                          <span>Common Search Patterns by Mode</span>
                        </h4>
                        {analyticsData.searchPatterns?.length > 0 ? (
                          <div className="space-y-6">
                            {/* Mode coverage notice */}
                            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 text-sm">
                              <p className="text-blue-300">
                                <span className="font-medium">Mode Tracking:</span> Mode information is available for recent interactions. 
                                "Unknown" indicates interactions before mode tracking was implemented.
                              </p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {analyticsData.searchPatterns.slice(0, 9).map((pattern, index) => (
                                <div key={index} className="bg-gray-800/50 rounded-lg p-4">
                                  <p className="text-white font-medium mb-3">"{pattern.pattern}"</p>
                                  
                                  {/* Total frequency */}
                                  <div className="flex items-center justify-between text-sm mb-3">
                                    <span className="text-gray-400">Total:</span>
                                    <span className="text-primary font-bold">{pattern.frequency}</span>
                                  </div>
                                  
                                  {/* Mode breakdown */}
                                  {pattern.modeBreakdown && (
                                    <div className="space-y-2 mb-3">
                                      {/* Search mode */}
                                      {pattern.modeBreakdown.search > 0 && (
                                        <div className="flex items-center justify-between text-xs">
                                          <div className="flex items-center space-x-2">
                                            <div className="w-2 h-2 bg-primary rounded-full"></div>
                                            <span className="text-gray-400">Search</span>
                                          </div>
                                          <span className="text-primary font-medium">
                                            {pattern.modeBreakdown.search} ({pattern.modeBreakdown.total > 0 ? Math.round((pattern.modeBreakdown.search / pattern.modeBreakdown.total) * 100) : 0}%)
                                          </span>
                                        </div>
                                      )}
                                      
                                      {/* Coach mode */}
                                      {pattern.modeBreakdown.coach > 0 && (
                                        <div className="flex items-center justify-between text-xs">
                                          <div className="flex items-center space-x-2">
                                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                            <span className="text-gray-400">Coach</span>
                                          </div>
                                          <span className="text-green-400 font-medium">
                                            {pattern.modeBreakdown.coach} ({pattern.modeBreakdown.total > 0 ? Math.round((pattern.modeBreakdown.coach / pattern.modeBreakdown.total) * 100) : 0}%)
                                          </span>
                                        </div>
                                      )}
                                      
                                      {/* Unknown mode */}
                                      {pattern.modeBreakdown.unknown > 0 && (
                                        <div className="flex items-center justify-between text-xs">
                                          <div className="flex items-center space-x-2">
                                            <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                                            <span className="text-gray-400">Unknown</span>
                                          </div>
                                          <span className="text-gray-500 font-medium">
                                            {pattern.modeBreakdown.unknown} ({pattern.modeBreakdown.total > 0 ? Math.round((pattern.modeBreakdown.unknown / pattern.modeBreakdown.total) * 100) : 0}%)
                                          </span>
                                        </div>
                                      )}
                                      
                                      {/* Visual bar - only show if we have mode data */}
                                      {(pattern.modeBreakdown.search > 0 || pattern.modeBreakdown.coach > 0) && (
                                        <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
                                          <div className="h-full flex">
                                            {pattern.modeBreakdown.total > 0 && (
                                              <>
                                                {pattern.modeBreakdown.search > 0 && (
                                                  <div 
                                                    className="bg-primary h-full transition-all duration-300"
                                                    style={{ width: `${(pattern.modeBreakdown.search / pattern.modeBreakdown.total) * 100}%` }}
                                                  />
                                                )}
                                                {pattern.modeBreakdown.coach > 0 && (
                                                  <div 
                                                    className="bg-green-500 h-full transition-all duration-300"
                                                    style={{ width: `${(pattern.modeBreakdown.coach / pattern.modeBreakdown.total) * 100}%` }}
                                                  />
                                                )}
                                                {pattern.modeBreakdown.unknown > 0 && (
                                                  <div 
                                                    className="bg-gray-500 h-full transition-all duration-300"
                                                    style={{ width: `${(pattern.modeBreakdown.unknown / pattern.modeBreakdown.total) * 100}%` }}
                                                  />
                                                )}
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Example */}
                                  {pattern.example && (
                                    <p className="text-xs text-gray-500 mt-2 italic">
                                      e.g., "{pattern.example}"
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-gray-400 text-center py-4">No search patterns identified yet</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Manage Indexed Section */}
              {activeSection === 'manage' && (
                <div>
                  {/* Header with stats and controls */}
                  <div className="mb-6 p-4 bg-gray-700/30 rounded-xl">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div>
                        <h3 className="font-medium text-white">Manage Indexed Documents</h3>
                        <p className="text-sm text-gray-400 mt-1">
                          {filteredDocuments.length} of {indexedDocuments.length} documents
                        </p>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-3">
                        {/* Search */}
                        <div className="relative">
                          <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search documents..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                          />
                        </div>
                        
                        {/* Filters */}
                        <select
                          value={selectedType}
                          onChange={(e) => setSelectedType(e.target.value)}
                          className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                        >
                          <option value="all">All Types</option>
                          <option value="playbook">Playbook</option>
                          <option value="openverse_resource">Openverse</option>
                          <option value="openorg_resource">Open Org Resource</option>
                          <option value="external_url">External URL</option>
                          <option value="newsletter">Newsletter</option>
                        </select>
                        
                        <select
                          value={selectedSource}
                          onChange={(e) => setSelectedSource(e.target.value)}
                          className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                        >
                          <option value="all">All Sources</option>
                          <option value="playbook_parser">Playbooks</option>
                          <option value="openverse_api">Openverse API</option>
                          <option value="external_website">External Sites</option>
                          <option value="newsletter">Newsletters</option>
                        </select>
                        
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="title">Title A-Z</option>
                          <option value="type">Type</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {documentsLoading ? (
                    <div className="text-center py-12">
                      <ArrowPathIcon className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-400">Loading documents...</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-green-900/30 to-gray-800/50 rounded-lg p-4 border border-green-700/30">
                          <div className="flex items-center space-x-3">
                            <BookOpenIcon className="w-6 h-6 text-green-400" />
                            <div>
                              <p className="text-lg font-bold text-white">
                                {indexedDocuments.filter(d => d.type === 'playbook').length}
                              </p>
                              <p className="text-xs text-green-300">Playbooks</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-purple-900/30 to-gray-800/50 rounded-lg p-4 border border-purple-700/30">
                          <div className="flex items-center space-x-3">
                            <SparklesIcon className="w-6 h-6 text-purple-400" />
                            <div>
                              <p className="text-lg font-bold text-white">
                                {indexedDocuments.filter(d => d.content_source === 'openverse_api').length}
                              </p>
                              <p className="text-xs text-purple-300">Openverse</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-yellow-900/30 to-gray-800/50 rounded-lg p-4 border border-yellow-700/30">
                          <div className="flex items-center space-x-3">
                            <EnvelopeIcon className="w-6 h-6 text-yellow-400" />
                            <div>
                              <p className="text-lg font-bold text-white">
                                {indexedDocuments.filter(d => d.content_source === 'newsletter').length}
                              </p>
                              <p className="text-xs text-yellow-300">Newsletters</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-blue-900/30 to-gray-800/50 rounded-lg p-4 border border-blue-700/30">
                          <div className="flex items-center space-x-3">
                            <LinkIcon className="w-6 h-6 text-blue-400" />
                            <div>
                              <p className="text-lg font-bold text-white">
                                {indexedDocuments.filter(d => d.content_source === 'external_website').length}
                              </p>
                              <p className="text-xs text-blue-300">External</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Documents List */}
                      <div className="bg-gray-800/50 rounded-xl overflow-hidden">
                        {filteredDocuments.length === 0 ? (
                          <div className="text-center py-12">
                            <DocumentTextIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                            <p className="text-gray-400">No documents found matching your criteria</p>
                          </div>
                        ) : (
                          <>
                            {/* Table Header */}
                            <div className="bg-gray-700/50 px-6 py-3 border-b border-gray-600">
                              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-300">
                                <div className="col-span-4">Title</div>
                                <div className="col-span-2">Type</div>
                                <div className="col-span-2">Source</div>
                                <div className="col-span-1">Chunks</div>
                                <div className="col-span-2">Date</div>
                                <div className="col-span-1">Actions</div>
                              </div>
                            </div>
                            
                            {/* Table Body */}
                            <div className="divide-y divide-gray-700">
                              {filteredDocuments
                                .slice((currentPage - 1) * documentsPerPage, currentPage * documentsPerPage)
                                .map((doc) => (
                                <div key={doc.id} className="group px-6 py-4 hover:bg-gray-700/30 transition-colors">
                                  <div className="grid grid-cols-12 gap-4 items-center">
                                    {/* Title */}
                                    <div className="col-span-4">
                                                            {editingTitle === doc.id ? (
                        <div className="flex items-center space-x-1">
                          {savingTitle === doc.id ? (
                            <div className="flex items-center space-x-2 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm">
                              <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                              <span>Saving...</span>
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={editTitleValue}
                              onChange={(e) => setEditTitleValue(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  autoSaveTitle(doc.id, editTitleValue);
                                }
                              }}
                              onBlur={() => {
                                if (editTitleValue.trim() !== doc.title.trim()) {
                                  autoSaveTitle(doc.id, editTitleValue);
                                } else {
                                  setEditingTitle(null);
                                  setEditTitleValue('');
                                }
                              }}
                              className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-primary"
                              autoFocus
                              placeholder="Enter title and press Enter or click away to save"
                            />
                          )}
                          <button
                            onClick={cancelEditingTitle}
                            className="p-1 text-gray-400 hover:text-red-400"
                            title="Cancel editing"
                          >
                            <XCircleIcon className="w-3 h-3" />
                          </button>
                        </div>
                                      ) : (
                                        <div className="flex items-center space-x-2">
                                          <span className="text-white font-medium text-sm truncate" title={doc.title}>
                                            {doc.title}
                                          </span>
                                          {doc.url && (
                                            <a
                                              href={doc.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-gray-400 hover:text-white"
                                            >
                                              <LinkIcon className="w-4 h-4" />
                                            </a>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Type */}
                                    <div className="col-span-2">
                                      {editingType === doc.id ? (
                                        <div className="flex items-center space-x-1">
                                          {savingType === doc.id ? (
                                            <div className="flex items-center space-x-2 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs">
                                              <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                                              <span>Saving...</span>
                                            </div>
                                          ) : (
                                            <select
                                              value={editTypeValue}
                                              onChange={(e) => {
                                                const newType = e.target.value;
                                                console.log('🎯 Type dropdown changed:', { 
                                                  docId: doc.id, 
                                                  docTitle: doc.title, 
                                                  oldType: doc.type, 
                                                  newType 
                                                });
                                                setEditTypeValue(newType);
                                                autoSaveType(doc.id, newType);
                                              }}
                                              className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:border-primary"
                                            >
                                              <option value="playbook">playbook</option>
                                              <option value="openverse_resource">openverse_resource</option>
                                              <option value="openorg_resource">openorg_resource</option>
                                              <option value="newsletter">newsletter</option>
                                              <option value="external_url">external_url</option>
                                              <option value="document">document</option>
                                              <option value="resource">resource</option>
                                              <option value="discount">discount</option>
                                            </select>
                                          )}
                                          <button
                                            onClick={cancelEditingType}
                                            className="p-1 text-gray-400 hover:text-red-400"
                                            title="Cancel editing"
                                          >
                                            <XCircleIcon className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex items-center space-x-1">
                                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                                            doc.type === 'playbook' ? 'bg-green-900/30 text-green-300' :
                                            doc.type === 'openverse_resource' ? 'bg-purple-900/30 text-purple-300' :
                                            doc.type === 'openorg_resource' ? 'bg-orange-900/30 text-orange-300' :
                                            doc.type === 'newsletter' ? 'bg-yellow-900/30 text-yellow-300' :
                                            'bg-blue-900/30 text-blue-300'
                                          }`}>
                                            {doc.type?.replace('_', ' ') || 'Unknown'}
                                          </span>
                                          <button
                                            onClick={() => startEditingType(doc)}
                                            className="p-1 text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                                            title="Click to edit type (auto-saves)"
                                          >
                                            <PencilIcon className="w-3 h-3" />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Source */}
                                    <div className="col-span-2">
                                      {editingSource === doc.id ? (
                                        <div className="flex items-center space-x-1">
                                          {savingSource === doc.id ? (
                                            <div className="flex items-center space-x-2 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs">
                                              <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                                              <span>Saving...</span>
                                            </div>
                                          ) : (
                                            <select
                                              value={editSourceValue}
                                              onChange={(e) => {
                                                const newSource = e.target.value;
                                                setEditSourceValue(newSource);
                                                autoSaveSource(doc.id, newSource);
                                              }}
                                              className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:border-primary"
                                            >
                                              <option value="internal_playbooks">internal_playbooks</option>
                                              <option value="openverse_api">openverse_api</option>
                                              <option value="newsletter">newsletter</option>
                                              <option value="external_website">external_website</option>
                                              <option value="marketplace_discounts">marketplace_discounts</option>
                                            </select>
                                          )}
                                          <button
                                            onClick={cancelEditingSource}
                                            className="p-1 text-gray-400 hover:text-red-400"
                                            title="Cancel editing"
                                          >
                                            <XCircleIcon className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex items-center space-x-1">
                                          <span className="text-gray-400 text-sm">
                                            {doc.content_source?.replace('_', ' ') || 'Unknown'}
                                          </span>
                                          <button
                                            onClick={() => startEditingSource(doc)}
                                            className="p-1 text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                                            title="Click to edit source (auto-saves)"
                                          >
                                            <PencilIcon className="w-3 h-3" />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Chunks */}
                                    <div className="col-span-1">
                                      <span className="text-gray-300 text-sm font-mono">
                                        {doc.chunk_count || 0}
                                      </span>
                                    </div>
                                    
                                    {/* Date */}
                                    <div className="col-span-2">
                                      <span className="text-gray-400 text-sm">
                                        {new Date(doc.created_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                    
                                    {/* Actions */}
                                    <div className="col-span-1">
                                      <div className="flex items-center space-x-1">
                                        <button
                                          onClick={() => startEditingTitle(doc)}
                                          className="p-1 text-gray-400 hover:text-white transition-colors"
                                          title="Click to edit title (auto-saves on Enter or blur)"
                                        >
                                          <PencilIcon className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => deleteDocument(doc.id)}
                                          className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                                          title="Delete document"
                                        >
                                          <TrashIcon className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Pagination */}
                            {filteredDocuments.length > documentsPerPage && (
                              <div className="bg-gray-700/50 px-6 py-4 border-t border-gray-600">
                                <div className="flex items-center justify-between">
                                  <div className="text-sm text-gray-400">
                                    Showing {((currentPage - 1) * documentsPerPage) + 1} to {Math.min(currentPage * documentsPerPage, filteredDocuments.length)} of {filteredDocuments.length} documents
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                      disabled={currentPage === 1}
                                      className="px-3 py-1 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
                                    >
                                      Previous
                                    </button>
                                    <span className="px-3 py-1 text-gray-300">
                                      Page {currentPage} of {Math.ceil(filteredDocuments.length / documentsPerPage)}
                                    </span>
                                    <button
                                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredDocuments.length / documentsPerPage)))}
                                      disabled={currentPage === Math.ceil(filteredDocuments.length / documentsPerPage)}
                                      className="px-3 py-1 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
                                    >
                                      Next
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin; 